import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/hooks';
import { useNavigate } from 'react-router-dom';

import { USER_ROLES, COURSE_TYPES } from 'constants/common';

import * as api from 'api/methods';

import { userSelector } from 'store/auth/selectors';

import { useSearch } from 'hooks/useSearch';
import { useModal } from 'hooks/useModal';

import { getTranslation } from 'helpers/getTranslation';

import {
  getCoursesTableColumns,
  getCoursesTableColumnsRootAdmin,
} from 'pages/CoursesPage/CoursesPage.tableConfig';
import {
  getTrainingsTableColumnsRootAdmin,
  getTrainingsTableColumns,
} from 'pages/TrainingsPage/TrainingsPage.tableConfig';
import {
  getDailyChallengesTableColumnsRootAdmin,
  getDailyChallengesTableColumns,
} from 'pages/DailyChallengesPage/DailyChallengesPage.tableConfig';
import {
  getArcadesTableColumnsRootAdmin,
  getArcadesTableColumns,
} from 'pages/ArcadesPage/ArcadesPage.tableConfig';

const getColumnsByType = ({
  isRootAdmin,
  type,
}: {
  isRootAdmin: any;
  type: any;
}) => {
  switch (type) {
    case COURSE_TYPES.COURSE: {
      return isRootAdmin
        ? getCoursesTableColumnsRootAdmin()
        : getCoursesTableColumns();
    }
    case COURSE_TYPES.TRAINING: {
      return isRootAdmin
        ? getTrainingsTableColumnsRootAdmin()
        : getTrainingsTableColumns();
    }
    case COURSE_TYPES.DAILY_CHALLENGE: {
      return isRootAdmin
        ? getDailyChallengesTableColumnsRootAdmin()
        : getDailyChallengesTableColumns();
    }
    case COURSE_TYPES.ARCADE: {
      return isRootAdmin
        ? getArcadesTableColumnsRootAdmin()
        : getArcadesTableColumns();
    }
    case COURSE_TYPES.LEARNING_PATH: {
      return isRootAdmin
        ? getCoursesTableColumnsRootAdmin()
        : getCoursesTableColumns();
    }
    default: {
      return isRootAdmin
        ? getCoursesTableColumnsRootAdmin()
        : getCoursesTableColumns();
    }
  }
};

const _getRemoveCourseTitleByType = (type: any) => {
  switch (type) {
    case COURSE_TYPES.COURSE: {
      return getTranslation('hooks.use_courses.remove_course.title');
    }
    case COURSE_TYPES.TRAINING: {
      return getTranslation('hooks.use_courses.remove_training.title');
    }
    case COURSE_TYPES.DAILY_CHALLENGE: {
      return getTranslation('hooks.use_courses.remove_training.title');
    }
    case COURSE_TYPES.ARCADE: {
      return getTranslation('hooks.use_courses.remove_arcade.title');
    }
    case COURSE_TYPES.LEARNING_PATH: {
      return getTranslation('hooks.use_courses.remove_course.title');
    }
    default: {
      return getTranslation('hooks.use_courses.remove_course.title');
    }
  }
};

const _getRemoveCourseTextByType = (type: any) => {
  switch (type) {
    case COURSE_TYPES.COURSE: {
      return getTranslation('hooks.use_courses.remove_course.text');
    }
    case COURSE_TYPES.TRAINING: {
      return getTranslation('hooks.use_courses.remove_training.text');
    }
    case COURSE_TYPES.DAILY_CHALLENGE: {
      return getTranslation('hooks.use_courses.remove_training.text');
    }
    case COURSE_TYPES.ARCADE: {
      return getTranslation('hooks.use_courses.remove_arcade.text');
    }
    case COURSE_TYPES.LEARNING_PATH: {
      return getTranslation('hooks.use_courses.remove_course.text');
    }
    default: {
      return getTranslation('hooks.use_courses.remove_course.text');
    }
  }
};

const DEFAULT_ITEMS_PER_PAGE = 50;

export const useCourses = ({
  getItems,
  removeItemById,
  createItem,
  itemsPerPage,
  type,
  pathPrefix,
  excludeArchivedByDefault = false,
}: {
  getItems: any;
  removeItemById: any;
  createItem: any;
  itemsPerPage: any;
  type: any;
  pathPrefix: any;
  excludeArchivedByDefault?: boolean;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const user = useSelector(userSelector);

  const [createOpen, openCreate, closeCreate] = useModal({
    defaultOpen: false,
  });
  const [
    removeConfirmOpen,
    openRemoveConfirm,
    closeRemoveConfirm,
    removeConfirmData,
  ] = useModal({
    defaultOpen: false,
  });
  const [removeOpen, openRemove, closeRemove, removeData] = useModal({
    defaultOpen: false,
  });

  const [loading, setLoading] = useState(true);
  const [triggerGetItems, setTriggerGetItems] = useState(null);
  const [sortParams, setSortParams] = useState({
    sortBy: 'lastActionAt',
    sortDirection: 'desc',
  });

  const [searchQuery, handleSearchChange] = useSearch();
  const [showArchived, setShowArchived] = useState(false);

  const _getListParams = (overrides: Record<string, any> = {}) => {
    const params: Record<string, any> = {
      type,
      sortBy: sortParams.sortBy,
      sortDirection: sortParams.sortDirection,
      ...overrides,
    };

    if (searchQuery) {
      params.query = searchQuery;
    }

    if (excludeArchivedByDefault && !showArchived) {
      params.excludeStatus = 'ARCHIVED';
    }

    return params;
  };

  const listQueryParams = _getListParams();

  useEffect(() => {
    const _getItems = async () => {
      try {
        await dispatch(
          getItems({
            page: 1,
            limit: DEFAULT_ITEMS_PER_PAGE,
            ..._getListParams(),
          }),
        ).unwrap();
      } catch (error) {
        console.warn(error);
      }

      setLoading(false);
    };

    _getItems();
  }, [searchQuery, triggerGetItems, showArchived]);

  const handleShowArchivedChange = (event: any) => {
    setShowArchived(event.target.checked);
  };

  const handleRowClick = (itemId: string) => {
    navigate(`${pathPrefix}/${itemId}`);
  };

  const handleEditClick = (item: any) => {
    navigate(`${pathPrefix}/${item.id}`);
  };

  const handleRemoveClick = (item: any) => {
    openRemoveConfirm({
      title: _getRemoveCourseTitleByType(item.type),
      text: _getRemoveCourseTextByType(item.type),
      data: {
        item,
      },
    });
  };

  const handleCopyClick = async (course: any) => {
    try {
      await api.copyCourseById(course.id);

      setTriggerGetItems(new Date());
    } catch (error) {
      console.warn(error);
    }
  };

  const handleConfirmRemoveClick = ({ item }: { item: any }) => {
    closeRemoveConfirm();

    openRemove({
      requestParams: item.id,
      onRemoved: () => {
        closeRemove();

        dispatch(removeItemById(item.id));
      },
    });
  };

  const handleCreateClick = () => {
    openCreate();
  };

  const handleContextItemClick = async (contextItem: any) => {
    try {
      const defaultData = {
        type,
        platform: contextItem.value,
        schoolId: user.schoolId,
      };

      const item = await createItem(defaultData);

      navigate(`${pathPrefix}/${item.id}`);
    } catch (error) {
      console.warn(error); // TODO: catch error
    }
  };

  const handleSortChange = async (nextSortBy: any) => {
    try {
      await dispatch(
        getItems({
          page: 1,
          limit: itemsPerPage,
          ..._getListParams({
            sortBy: nextSortBy,
            sortDirection: sortParams.sortDirection === 'asc' ? 'desc' : 'asc',
          }),
        }),
      ).unwrap();

      setSortParams((prevState: any) => ({
        sortBy: nextSortBy,
        sortDirection: prevState.sortDirection === 'asc' ? 'desc' : 'asc',
      }));
    } catch (error) {
      console.warn(error);
    }
  };

  const isRootAdmin = user.role === USER_ROLES.ROOT_ADMIN;
  const columns = getColumnsByType({ isRootAdmin, type });

  return {
    loading,
    isRootAdmin,
    createOpen,
    removeOpen,
    removeConfirmOpen,
    sortParams,
    columns,
    removeConfirmData,
    removeData,
    closeCreate,
    closeRemoveConfirm,
    closeRemove,
    onSearchChange: handleSearchChange,
    onSortChange: handleSortChange,
    onContextItemClick: handleContextItemClick,
    onRowClick: handleRowClick,
    onEditClick: handleEditClick,
    onRemoveClick: handleRemoveClick,
    onCopyClick: handleCopyClick,
    onConfirmRemoveClick: handleConfirmRemoveClick,
    onCreateClick: handleCreateClick,
    showArchived,
    onShowArchivedChange: handleShowArchivedChange,
    listQueryParams,
  };
};
